import { FC, useRef, useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import ReplayIcon from '@mui/icons-material/Replay'
import CheckIcon from '@mui/icons-material/Check'
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import { legacyTheme as theme } from '@/apps/legacy/shared/styles/theme'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { Yakureki } from '@/shared/types/yakurekiCore'
import { Pharmacist } from '@/features/auth/types/pharmacy'
import { useGroupRecordAudio } from '@/features/yakureki/hooks/useGroupRecordAudio'

interface GroupRecordingModalProps {
  open: boolean
  onClose: () => void
  selectedPatients: Yakureki[]
  selectedIds: string[]
  onComplete: () => void
  pharmacistId?: string
  setPharmacistId?: (id: string) => void
  pharmacistList?: Pharmacist[]
}

export const GroupRecordingModal: FC<GroupRecordingModalProps> = ({
  open,
  onClose,
  selectedPatients,
  selectedIds,
  onComplete,
  pharmacistId,
  setPharmacistId,
  pharmacistList = [],
}) => {
  const {
    isRecording,
    audioURL,
    isSending,
    permissionStatus,
    startRecording,
    stopRecording,
    cancelRecording,
    clearAudio,
    sendGroupAudio,
  } = useGroupRecordAudio(onComplete, pharmacistId)

  const audioRef = useRef<HTMLAudioElement>(null)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  // 患者ごとの「同伴/代理人有」flag (event_id → bool). 録音 start で stream API に渡される。
  const [companionMap, setCompanionMap] = useState<Record<string, boolean>>({})

  const hasCompanionOrSubstituteMap = useMemo(() => {
    const result: Record<string, boolean> = {}
    selectedIds.forEach((id) => {
      if (companionMap[id]) result[id] = true
    })
    return Object.keys(result).length > 0 ? result : undefined
  }, [companionMap, selectedIds])

  const handleClose = () => {
    if (isRecording) {
      cancelRecording()
    }
    if (audioURL) {
      clearAudio()
    }
    setCompanionMap({})
    onClose()
  }

  const handleSend = async () => {
    await sendGroupAudio(selectedIds)
  }

  const toggleCompanion = (eventId: string) => {
    setCompanionMap((prev) => ({ ...prev, [eventId]: !prev[eventId] }))
  }

  const handlePlaySound = async () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause()
        setIsAudioPlaying(false)
      } else {
        await audioRef.current.play()
        setIsAudioPlaying(true)
      }
    }
  }

  return (
    <Dialog
      open={open}
      onClose={isSending ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 500,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">
          まとめて録音（{selectedPatients.length}名）
        </Typography>
        <IconButton onClick={handleClose} disabled={isSending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
          {/* 選択済み患者: 各患者に「同伴/代理人有」チェックボックスを露出 */}
          <Stack spacing={0.5} sx={{ width: '100%', maxWidth: 360 }}>
            {selectedPatients.map((p) => (
              <Stack
                key={p.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colorTokens.text.secondary }}
                >
                  {p.label}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!companionMap[p.id]}
                      onChange={() => toggleCompanion(p.id)}
                      disabled={isRecording || isSending || !!audioURL}
                      inputProps={{
                        'aria-label': `${p.label} 同伴/代理人有`,
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption">同伴/代理人有</Typography>
                  }
                  sx={{ mr: 0 }}
                />
              </Stack>
            ))}
          </Stack>

          {/* 録音UI */}
          {audioURL ? (
            // 録音完了: プレビュー + アクションボタン
            <Stack alignItems="center" spacing={2}>
              <audio
                ref={audioRef}
                src={audioURL}
                onEnded={() => setIsAudioPlaying(false)}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 200,
                  height: 200,
                  bgcolor: colorTokens.grey[100],
                  borderRadius: '50%',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                  },
                }}
                onClick={handlePlaySound}
              >
                {isAudioPlaying ? (
                  <PauseCircleIcon
                    style={{ fontSize: 100, color: theme.palette.primary.main }}
                  />
                ) : (
                  <PlayCircleFilledWhiteIcon
                    style={{ fontSize: 100, color: theme.palette.primary.main }}
                  />
                )}
              </Box>

              <Stack
                direction="row"
                spacing={2}
                sx={{ width: '100%', maxWidth: 360 }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  onClick={clearAudio}
                  disabled={isSending}
                  startIcon={<ReplayIcon />}
                  sx={{
                    color: '#fff',
                    bgcolor: theme.palette.secondary.dark,
                    '&:hover': {
                      bgcolor: theme.palette.secondary.main,
                    },
                    borderRadius: 28,
                    py: 1.2,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  録り直し
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSend}
                  disabled={isSending}
                  startIcon={<CheckIcon />}
                  sx={{
                    color: '#fff',
                    bgcolor: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: theme.palette.success.dark,
                    },
                    borderRadius: 28,
                    py: 1.2,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  {isSending ? 'アップロード中...' : 'SOAP作成'}
                </Button>
              </Stack>
            </Stack>
          ) : isRecording ? (
            // 録音中
            <Stack alignItems="center" spacing={2}>
              <Typography
                sx={{
                  color: theme.palette.error.main,
                }}
              >
                ● 録音中...
              </Typography>
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  borderRadius: '50%',
                  backgroundColor: colorTokens.special.recordingBackground,
                  boxShadow: '0 4px 12px rgba(101, 154, 210, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                  },
                }}
                onClick={stopRecording}
              >
                <MicIcon
                  style={{ fontSize: 100, color: colorTokens.special.micIcon }}
                />
                <Typography
                  sx={{
                    color: colorTokens.special.micIcon,
                    mt: 1,
                  }}
                >
                  クリックして停止
                </Typography>
              </Box>
            </Stack>
          ) : (
            // 録音前
            <Stack alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  borderRadius: '50%',
                  backgroundColor: colorTokens.grey[100],
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  cursor: permissionStatus === 'denied' ? 'default' : 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover':
                    permissionStatus !== 'denied'
                      ? {
                          transform: 'scale(1.03)',
                          boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                        }
                      : {},
                }}
                onClick={
                  permissionStatus !== 'denied'
                    ? () =>
                        startRecording({
                          yakurekiIds: selectedIds,
                          hasCompanionOrSubstituteMap,
                        })
                    : undefined
                }
              >
                <MicIcon
                  style={{ fontSize: 100, color: colorTokens.special.micIcon }}
                />
                {permissionStatus !== 'denied' && (
                  <Typography
                    sx={{
                      color: colorTokens.special.micIcon,
                      mt: 1,
                    }}
                  >
                    クリックして録音
                  </Typography>
                )}
              </Box>
              {permissionStatus === 'denied' && (
                <Typography sx={{ color: theme.palette.secondary.dark }}>
                  マイクが接続できていません
                </Typography>
              )}
              {pharmacistList.length >= 1 && setPharmacistId && (
                <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
                  <InputLabel>担当薬剤師</InputLabel>
                  <Select
                    value={pharmacistId || ''}
                    label="担当薬剤師"
                    onChange={(e) => setPharmacistId(e.target.value)}
                  >
                    {pharmacistList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
